import { loggedFetch } from "../utils/centralized-logging/loggedFetch";

const getPagoApiBaseUrl = () => {
  const base = process.env.REACT_APP_API_URL_TREE_SCALE || "";
  return base.endsWith("/") ? base : base + "/";
};

/**
 * Login al API de pago. Usa REACT_APP_API_URL_TREE_SCALE, REACT_APP_USER_KEY_PAGO_SERVICE,
 * REACT_APP_PAGO_AUTH_USERNAME, REACT_APP_PAGO_AUTH_PASSWORD.
 * @returns {Promise<{ok: boolean, data?: {accessToken: string, refreshToken: string}, error?: string}>}
 */
async function loginPagoApi() {
  const baseUrl = getPagoApiBaseUrl();
  const url = `${baseUrl}pagoservice/api/v1/pago/auth/login`;
  const userKey = process.env.REACT_APP_USER_KEY_PAGO_SERVICE;
  const username = process.env.REACT_APP_PAGO_AUTH_USERNAME;
  const password = process.env.REACT_APP_PAGO_AUTH_PASSWORD;

  if (!userKey || !username || !password) {
    return { ok: false, error: "Faltan variables de entorno para login pago" };
  }

  try {
    const res = await loggedFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        accept: "text/plain",
        user_key: userKey,
      },
      body: JSON.stringify({ username, password }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: json?.meta?.descripcion || res.statusText || "Error en login",
      };
    }
    const accessToken = json?.data?.accessToken;
    const refreshToken = json?.data?.refreshToken;
    if (!accessToken) {
      return { ok: false, error: "Login sin accessToken" };
    }
    return { ok: true, data: { accessToken, refreshToken } };
  } catch (err) {
    return { ok: false, error: err.message || "Error de red en login" };
  }
}

/**
 * Consulta balance de póliza en el nuevo API de pago.
 * @param {string} numeroPoliza
 * @param {string} accessToken
 * @param {string|null|undefined} [numeroFactura] - Si tiene valor, se envía como query ?numeroFactura=...
 * @returns {Promise<{ok: boolean, status: number, data?: object}>}
 */
async function consultarPolizaBalance(numeroPoliza, accessToken, numeroFactura) {
  const baseUrl = getPagoApiBaseUrl();
  let url = `${baseUrl}pagoservice/api/v1/pago/polizas/${encodeURIComponent(numeroPoliza)}/balance`;
  if (numeroFactura != null && numeroFactura !== "") {
    url += `?numeroFactura=${encodeURIComponent(numeroFactura)}`;
  }
  const userKey = process.env.REACT_APP_USER_KEY_PAGO_SERVICE;

  try {
    const res = await loggedFetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: accessToken,
        user_key: userKey || "",
      },
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  } catch (err) {
    return { ok: false, status: 0, data: { meta: { descripcion: err.message } } };
  }
}

/**
 * Mapea el request del front al body del API de pago (anoExpiracion -> anioExpiracion, numeroSeguridadTarjeta -> numeroSeguridad, mes 2 dígitos).
 */
function mapRequestToPagoBody(request) {
  const anio = request.anoExpiracion;
  const anioExpiracion =
    typeof anio === "string" && anio.length === 2 ? "20" + anio : String(anio ?? "");
  return {
    idTransaccion: request.idTransaccion,
    numeroTarjeta: request.numeroTarjeta,
    mesExpiracion:
      String(request.mesExpiracion ?? "").length >= 2
        ? String(request.mesExpiracion).slice(0, 2)
        : String(request.mesExpiracion ?? ""),
    anioExpiracion: anioExpiracion,
    numeroSeguridad: request.numeroSeguridadTarjeta ?? request.numeroSeguridad,
    montoPago: Number(request.montoPago) || 0,
    impuesto: Number(request.impuesto) || 0,
  };
}

/**
 * Registra el pago en el nuevo API. Normaliza la respuesta al formato que usa la pantalla:
 * { data: { codigoRespuesta, numeroRecibo, numeroAutorizacion, RestError? } }.
 * @param {object} request - { idTransaccion, numeroTarjeta, mesExpiracion, anoExpiracion, numeroSeguridadTarjeta, montoPago, impuesto }
 * @param {string} accessToken
 * @returns {Promise<{status: number, data: object}>}
 */
async function procesarYRegistrarPago(request, accessToken) {
  const baseUrl = getPagoApiBaseUrl();
  const url = `${baseUrl}pagoservice/api/v1/pago/pagos`;
  const userKey = process.env.REACT_APP_USER_KEY_PAGO_SERVICE;
  const body = mapRequestToPagoBody(request);

  try {
    const res = await loggedFetch(url, {
      method: "POST",
      headers: {
        accept: "text/plain",
        "Content-Type": "application/json",
        Authorization: accessToken,
        user_key: userKey || "",
      },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    const meta = json?.meta || {};
    const dataApi = json?.data;

    if (!res.ok) {
      return {
        status: res.status,
        data: {
          codigoRespuesta: meta.codigo ?? res.status,
          RestError: { errorMessage: meta.descripcion || res.statusText },
        },
      };
    }

    const codigoOk = meta.codigo === 200;
    return {
      status: res.status,
      data: {
        codigoRespuesta: codigoOk ? 0 : meta.codigo ?? res.status,
        numeroRecibo: dataApi?.numRecibo ?? dataApi?.numeroRecibo,
        numeroAutorizacion:
          dataApi?.numAutorizacion ?? dataApi?.numReferencia ?? dataApi?.numeroAutorizacion,
        totalPagado: dataApi?.totalPagado,
        poliza: dataApi?.poliza,
      },
    };
  } catch (err) {
    return {
      status: 0,
      data: {
        codigoRespuesta: -1,
        RestError: { errorMessage: err.message || "Error de red" },
      },
    };
  }
}

/**
 * Notifica el pago al intermediario.
 * POST {REACT_APP_API_URL_TREE_SCALE}pagoservices/api/Pagos/Intermediario/Notificar
 * @param {object} request - { poliza, nombreAsegurado, numeroAutorizacion, totalPagado }
 * @returns {Promise<{ok: boolean, status: number, data?: object}>}
 */
async function notificarPagoIntermediario(request) {
  const baseUrl = getPagoApiBaseUrl();
  const url = `${baseUrl}pagoservices/api/Pagos/Intermediario/Notificar`;
  const userKey = process.env.REACT_APP_USER_KEY_PAGO_SERVICE;

  try {
    const res = await loggedFetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        user_key: userKey || "",
      },
      body: JSON.stringify(request),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      data: { problem: err.message || "Error de red" },
    };
  }
}

export {
  loginPagoApi,
  consultarPolizaBalance,
  procesarYRegistrarPago,
  notificarPagoIntermediario,
};
